import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { DashboardService } from 'utility/services/dashboard'
import { InspectionService } from 'utility/services'
import { LoaderBar, PageHeader, TileCard, CardBody, AvatarCircle, EmptyBox, PaginationComponent } from 'components'
import { DashboardHeader } from 'components/DashboardHeader'
import { AppPrimaryButton } from 'elements'
import { Grid, Message } from 'semantic-ui-react'
import styled from 'styled-components'
// Header handled by shared DashboardHeader

// no local container styles; using simple wrappers

// use shared TileCard instead of local card styles

// use shared AvatarCircle for icon

// use default AppPrimaryButton for consistency

// use shared EmptyBox

// Header is handled by shared DashboardHeader

const DescriptionText = styled.div`
    color: #6c757d;
    ${(props) =>
        props.$expanded
            ? ''
            : `
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
    `}
`

const ReadMoreButton = styled.button`
    background: transparent;
    border: none;
    color: #2f2ccf;
    font-weight: 600;
    padding: 0;
    margin-top: 0.25rem;
    cursor: pointer;
    text-decoration: none;
    &:hover { text-decoration: underline; }
`

export const TemplateSelectionScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()

    const [templates, setTemplates] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [customerGroup, setCustomerGroup] = useState(null)
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        pageSize: 20,
    })
    const [expandedMap, setExpandedMap] = useState({})

    const toggleExpanded = id => {
        setExpandedMap(prev => ({ ...prev, [id]: !prev[id] }))
    }

    useEffect(() => {
        if (location.state?.customerGroup && location.state?.groupId) {
            setCustomerGroup(location.state.customerGroup)
            fetchTemplates(location.state.groupId)
        } else {
            navigate('/app/dashboard/groups')
        }
    }, [location.state, navigate])

    const fetchTemplates = async (groupId, pageNumber = 1) => {
        try {
            setLoading(true)
            setError('')
            console.log(
                'Fetching templates for groupId:',
                groupId,
                'page:',
                pageNumber,
            )
            const response = await DashboardService.getTemplatesByGroup(
                groupId,
                pageNumber,
                20,
            )
            console.log('Templates response:', response)
            setTemplates(response?.Items || [])

            // Update pagination state
            if (response?.MetaData) {
                setPagination({
                    currentPage: response.MetaData.CurrentPage || pageNumber,
                    totalPages: response.MetaData.LastPage || 1,
                    totalCount: response.MetaData.Total || 0,
                    pageSize: response.MetaData.PerPage || 20,
                })
            }
        } catch (err) {
            console.error('Error fetching templates:', err)
            setError('Failed to load templates')
        } finally {
            setLoading(false)
        }
    }

    const handleTemplateSelect = async template => {
        try {
            setLoading(true)
            console.log('Starting inspection for template:', template)
            console.log('Customer data:', location.state?.customer)
            console.log('CustomerGroup data:', customerGroup)

            // Check if we have customer data
            if (!location.state?.customer) {
                console.error('No customer data available')
                setError(
                    'Customer information is missing. Please go back and select a customer.',
                )
                return
            }

            // Use the same flow as the existing startInspection function
            const customerId =
                location.state?.customer?.unique_id ||
                location.state?.customer?.CustomerId ||
                location.state?.customer?.customer_id

            console.log('Using customerId:', customerId)

            if (!customerId) {
                console.error('No valid customer ID found')
                setError(
                    'Invalid customer ID. Please go back and select a customer.',
                )
                return
            }

            const result = await InspectionService.copyInspection({
                TemplateId: template.TemplateId,
                customerId,
            })

            console.log('CopyInspection result:', result)

            if (result?.AuditTemplateId) {
                // Navigate to the inspection page with the returned AuditTemplateId
                navigate(
                    `/app/templates/inspection/${result.AuditTemplateId}`,
                    {
                        state: {
                            customerGroup,
                            customer: location.state?.customer,
                            template,
                            fromDashboard: true,
                        },
                    },
                )
            } else {
                console.error('No AuditTemplateId returned from copyInspection')
                setError('Failed to create inspection. Please try again.')
            }
        } catch (error) {
            console.error('Error creating inspection:', error)
            setError('Failed to create inspection. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const handleBackClick = () => {
        navigate('/app/dashboard/customers', {
            state: {
                customerGroup,
                groupId: customerGroup?.CustomerGroupId,
            },
        })
    }

    if (loading) {
        return <LoaderBar content="Loading templates..." />
    }

    return (
        <>
            <div style={{ padding: '1rem' }}>
                <DashboardHeader
                    title="Restaurant Excellence Visit"
                    showBack
                    onBack={handleBackClick}
                    showHome
                    onHome={() => navigate('/app/dashboard/groups')}
                />
                <PageHeader
                title="Select Template"
                showBackButton
                onBackClick={handleBackClick}
            />

            {error && <Message negative content={error} />}

            {/* <Breadcrumb>
                <BreadcrumbText>
                    <strong>{customerGroup?.CustomerGroupName}</strong> → Select
                    Template
                </BreadcrumbText>
            </Breadcrumb> */}

            <div style={{ marginTop: '1rem' }}>
                <h3 style={{ marginBottom: '2rem' }}>Available Templates</h3>

                {templates.length === 0 && !loading ? (
                    <EmptyBox text="No Templates Found" />
                ) : (
                    <Grid stackable doubling columns={3} padded>
                        {templates.map((template, index) => (
                            <Grid.Column
                                key={
                                    template.TemplateUniqueId ||
                                    template.TemplateId ||
                                    index
                                }
                            >
                                <TileCard onClick={() => handleTemplateSelect(template)}>
                                    <CardBody>
                                        <AvatarCircle size="60px">
                                            {template.TemplateName?.charAt(0) || 'T'}
                                        </AvatarCircle>
                                        <h3 style={{ fontSize: '1.3rem', fontWeight: 600, marginBottom: '0.5rem', color: '#2c3e50' }}>
                                            {template.TemplateName || 'Unnamed Template'}
                                        </h3>
                                        <div style={{ marginBottom: '1rem' }} onClick={e => e.stopPropagation()}>
                                            <DescriptionText
                                                $expanded={expandedMap[
                                                    template.TemplateUniqueId || template.TemplateId || index
                                                ]}
                                            >
                                                {template.TemplateDescription || 'No description available'}
                                            </DescriptionText>
                                            {template.TemplateDescription && template.TemplateDescription.length > 0 && (
                                                <ReadMoreButton
                                                    type="button"
                                                    onClick={e => {
                                                        e.stopPropagation()
                                                        toggleExpanded(
                                                            template.TemplateUniqueId || template.TemplateId || index,
                                                        )
                                                    }}
                                                >
                                                    {expandedMap[
                                                        template.TemplateUniqueId || template.TemplateId || index
                                                    ]
                                                        ? 'Read less'
                                                        : 'Read more'}
                                                </ReadMoreButton>
                                            )}
                                        </div>
                                        <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                                            <span style={{ background: '#f8f9fa', padding: '0.25rem 0.75rem', borderRadius: '6px', fontSize: '0.9rem' }}>
                                                ID: {template.TemplateUniqueId || template.TemplateId}
                                            </span>
                                            <span style={{ background: '#f8f9fa', padding: '0.25rem 0.75rem', borderRadius: '6px', fontSize: '0.9rem' }}>
                                                Group: {template.CustomerGroupName}
                                            </span>
                                        </div>
                                        <AppPrimaryButton>Select Template</AppPrimaryButton>
                                    </CardBody>
                                </TileCard>
                            </Grid.Column>
                        ))}
                    </Grid>
                )}

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                        <PaginationComponent
                            activePage={pagination.currentPage}
                            totalCount={pagination.totalCount}
                            pageSize={pagination.pageSize}
                            pageChanged={page => fetchTemplates(customerGroup?.CustomerGroupId, page)}
                        />
                    </div>
                )}
            </div>
            </div>
        </>
    )
}
